import React, { useEffect, useState } from 'react';
import { Box, MenuItem, useTheme, TextField, Grid, Typography } from '@mui/material';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomPhoneInput from 'components/Fileds/CustomPhoneInput';
import { useAddUpdateCustomerMutation, useGetCustomerByPhoneMutation } from 'pages/dashboard/services';
import { useSelector } from 'react-redux';
import { useOrder } from 'context/OrderContext';
import CustomTimeTextField from 'components/Fileds/CustomTimeTextFiled';
import { fromOptions, untilOptions } from 'utils/constant';

const untilValues = untilOptions.map(opt => opt.value);


const ReserveTableSchema = Yup.object().shape({
    phone_number: Yup.string()
        .required('Mobile number is required')
        .matches(/^\+?[0-9]{7,15}$/, 'Enter a valid mobile number'),
    customer_name: Yup.string().required('Customer name is required'),
    persons: Yup.number()
        .typeError('Must be a number')
        .integer('Must be an integer')
        .min(1, 'At least one person required')
        .test('max-capacity', 'Table capacity exceeded!', function (persons) {
            const table = this.parent.table;
            //   if (!table || !table.capacity) return true;
            if (!table) return true; // skip if no table selected
            const capacity = table.totalCapacity ?? table.capacity; // use totalCapacity if available
            if (!capacity) return true; // skip if no capacity info
            return persons <= capacity;
        }),
    //   from: Yup.string().required('From time is required'),
    from: Yup.string().oneOf(['now', 'custom']).required('From selection is required'),
    customFrom: Yup.mixed()
        .nullable()
        //     .test(
        //         'custom-from-required',
        //         'From time is required',
        //         function (value) {
        //         const { from } = this.parent;
        //         if (from === 'custom') {
        //             return !!value; // must supply a time
        //         }
        //         return true;
        //         }
        // ),
        .test(
            'custom-from-future',
            'From time must be in the future',
            function (value) {
                const { from } = this.parent;
                if (from === 'custom' && value) {
                    const selected = new Date(value);
                    const now = new Date();
                    return selected.getTime() > now.getTime();
                }
                return true;
            }
        ),
    // until: Yup.string().required('Until time is required'),
    until: Yup.string()
        .oneOf(untilValues, 'Until time is required')
        .required('Until time is required'),

});

const ReserveTableModal = ({ open, onClose, initialData, onSubmit, loading }) => {
    const vendorCountry = useSelector((state) => state?.posData?.country_code)
    const autofetchmobilenumbercount = useSelector((state) => state?.posData?.autofetchmobilenumbercount ?? 10);
    const { customerData } = useOrder();
    const title = 'Reserve Table';
    const theme = useTheme();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryCode, setCountryCode] = useState(vendorCountry);
    const [customerName, setCustomerName] = useState("");
    const [isFetchingCustomer, setIsFetchingCustomer] = useState(false);
    const [originalCustomerName, setOriginalCustomerName] = useState("");
    const [customerId, setCustomerId] = useState(null);

    const [fromType, setFromType] = useState(initialData?.from || 'now');

    // Initialize API hooks
    const [getCustomerByPhone, { isLoading: isLoadingCustomer }] = useGetCustomerByPhoneMutation();
    const [addUpdateCustomer, { isLoading: isAddingCustomer }] = useAddUpdateCustomerMutation();

    const handlePhoneChange = async (value, country, formikHelpers) => {
        setPhoneNumber(value);

        // If we have a complete phone number, fetch customer data
        if (value && value.length >= autofetchmobilenumbercount) {
            setIsFetchingCustomer(true);

            try {
                // Call the API to search for customer by phone
                const response = await getCustomerByPhone({
                    country_code: countryCode,
                    phone: value,
                });

                // Check if we got customer data back
                if (response.data) {
                    const customerData = response?.data;
                    handleCustomerDataFetch(customerData, formikHelpers);
                    // setIsNewCustomer(false);
                } else {
                    // No customer found
                    setCustomerName("");
                    // setIsNewCustomer(true);
                }
            } catch (error) {
                console.error("Error fetching customer:", error);
                setCustomerName("");
                // setIsNewCustomer(true);
            } finally {
                setIsFetchingCustomer(false);
            }
        }
    };

    function handleCustomerDataFetch(customerData, formikHelpers) {
        if (customerData?.data?.customer) {
            const name = customerData?.data?.customer?.customer_full_name ? customerData?.data?.customer?.customer_full_name : customerData?.data?.customer?.full_name || "";
            setCustomerName(name);
            setOriginalCustomerName(name);
            setCustomerId(customerData?.data?.customer?.id || null);

            // Update Formik values without triggering validation
            if (formikHelpers && name) {
                formikHelpers.setFieldValue("customer_name", name);
            }
        } else {
            setCustomerName("");
            setOriginalCustomerName("");
            setCustomerId(null);
        }
    }

    const handleSubmit = async (values) => {
        const { persons, table, from, customFrom, until } = values;

        // normalize customFrom to ISO string if provided
        let customFromISO = null;
        if (from === 'custom' && customFrom) {
            customFromISO =
                typeof customFrom === 'string'
                    ? customFrom
                    : new Date(customFrom).toISOString();
        }

        try {
            let finalCustomerId = customerId;
            const isNewCustomer = !customerId; // if no ID → new customer
            const didNameChange = customerName && customerName !== originalCustomerName;
            // If new customer OR name changed → call API
            if (isNewCustomer || didNameChange) {
                const customerData = {
                    country_code: `+${countryCode}`,
                    phone: phoneNumber,
                    is_new: isNewCustomer,
                    full_name: customerName,
                };

                const response = await addUpdateCustomer({ body: customerData });

                if (response?.data?.data?.customerVendor?.id) {
                    finalCustomerId = response.data.data.customerVendor.id;
                    setCustomerId(finalCustomerId);
                    setOriginalCustomerName(customerName);
                }
            }


            const payloadCommon = {
                customer_id: finalCustomerId ?? null,
                guest_count: Number(persons),
                table_id: table?.id,
                from,
                child_ids: initialData?.child_ids ?? []
            };

            let payload;
            if (from === 'now') {
                // Case 1
                payload = {
                    ...payloadCommon,
                    until
                };
            } else {
                payload = {
                    ...payloadCommon,
                    customFrom: customFromISO,
                    until,
                };
            }
            onSubmit(payload);
        } catch (error) {
            console.error("Error in reservation submit:", error);
        }
    };


    const effectivePhone = open && customerData?.phone_number
        ? customerData.phone_number
        : "";

    return (
        <GenericDialog
            open={open}
            onClose={onClose}
            title={title}
            initialValues={{
                phone_number: '',
                customer_name: '',
                persons: 1,
                table: initialData,
                from: 'now',
                customFrom: null,
                until: 'manual',
            }}
            validationSchema={ReserveTableSchema}
            onSubmit={handleSubmit}
            submitLabel={'Continue'}
            loading={loading}
            requiredFields={['phone_number', 'customer_name', 'persons', 'from', 'until']}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched }) => (
                <>
                    <Box sx={{ mb: 2 }}>
                        <CustomTypography
                            label={"Customer Mobile Number"}
                            variant="body2"
                            sx={{
                                mb: 0.5, fontWeight: 600, fontSize: "14px",
                                color: theme.palette.typographyLabel.main
                            }}
                        />
                        <CustomPhoneInput
                            value={values?.phone_number}
                            onChange={(value, country) => {
                                handlePhoneChange(value, country, { setFieldValue, setFieldTouched });
                                setFieldValue("phone_number", value);
                                // if (country?.dialCode) {
                                setFieldValue(
                                    "country_code",
                                    vendorCountry);
                                // }
                                if (country?.iso2) {
                                    setFieldValue("country_ISO", country.iso2);
                                }
                            }}
                            width={"395px"}
                            isLoading={isLoadingCustomer}
                        />
                        {touched.phone_number && errors.phone_number && (
                            <Typography
                                variant="body2"
                                color="error"
                                sx={{ marginTop: "4px", fontSize: "12px" }}
                            >
                                {errors.phone_number}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <CustomTypography
                            label="Customer Name"
                            sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.typographyLabel.main }}
                        />
                        <CustomTextField
                            name="customer_name"
                            value={values.customer_name}
                            onChange={(e) => {
                                const newValue = e?.target?.value;
                                setCustomerName(newValue);
                                handleChange(e)
                            }}
                            onBlur={handleBlur}
                            fullWidth
                            placeholder="Customer Full Name"
                            error={touched.customer_name && Boolean(errors.customer_name)}
                            helperText={touched.customer_name && errors.customer_name}
                        />
                    </Box>

                    <Grid container sx={{ mb: 2 }} spacing={2}>
                        <Grid item xs={6}>
                            <CustomTypography
                                label="No. of Person(s)"
                                sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.typographyLabel.main, mb: 0.5 }}
                            />
                            <CustomTextField
                                name="persons"
                                type="number"
                                value={values.persons}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    // if (raw === "") {
                                    //     handleChange(e);
                                    //     // keep Formik’s value in sync
                                    //     setFieldValue("persons", "");
                                    //     return;
                                    // }
                                    // const num = Number(raw);
                                    // 2) Block anything below 1 (including negatives, zero, non-numeric)
                                    // if (!Number.isInteger(num) || num < 1) {
                                    //     return;
                                    // }
                                    // 3) Finally, accept it
                                    handleChange(e);
                                    setFieldValue("persons", raw);
                                }}
                                onBlur={handleBlur}
                                error={touched.persons && Boolean(errors.persons)}
                                helperText={touched.persons && errors.persons}
                                placeholder="1"
                                inputProps={{
                                    min: 1,
                                }}
                                sx={{
                                    // hide spinner in Chrome/Safari
                                    "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
                                    {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                    },
                                    // hide spinner in Firefox
                                    "input[type=number]": {
                                        MozAppearance: "textfield",
                                    },
                                    // width: "130px",
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <CustomTypography
                                label={"Select Table"}
                                variant="body2"
                                sx={{
                                    mb: 0.5, fontWeight: 600, fontSize: "14px",
                                    color: theme.palette.typographyLabel.main
                                }}
                            />
                            <CustomTextField
                                value={values?.table?.table_no}
                                name="table"
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <CustomTypography
                                label="From"
                                sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.typographyLabel.main }}
                            />
                            {values.from !== "custom" && <CustomSelect
                                name="from"
                                value={values.from}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setFieldValue('from', next);
                                    // Always reset until to default "manual" when from changes
                                    setFieldValue('until', 'manual');
                                    // Reset/prepare customFrom
                                    if (next === 'custom') {
                                        setFieldValue('customFrom', null);
                                    } else {
                                        setFieldValue('customFrom', null);
                                    }
                                }}
                                onBlur={handleBlur}
                                error={touched.from && errors.from}
                                helperText={touched.from && errors.from}
                                options={fromOptions /* should include {value:'now'},{value:'custom'} */}
                            />}
                            {values.from === 'custom' && (
                                <CustomTimeTextField
                                    name="customFrom"
                                    value={values.customFrom}
                                    // onChange={(newDate) => setFieldValue('customFrom', newDate)}
                                    onChange={(newDate) => {
                                        setFieldValue('customFrom', newDate);
                                        setFieldTouched('customFrom', true); // 👈 force touched
                                    }}
                                    error={touched.customFrom && Boolean(errors.customFrom)}
                                    helperText={touched.customFrom && errors.customFrom}
                                />
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <CustomTypography
                                label="Until"
                                sx={{ fontSize: 14, fontWeight: 600, color: theme.palette.typographyLabel.main }}
                            />
                            <CustomSelect
                                name="until"
                                value={values?.until || "manual"}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.until && errors.until}
                                helperText={touched.until && errors.until}
                                options={untilOptions}
                            />
                            {/* {fromType === 'custom' && (
                        <CustomTimeTextField
                            name="until"
                            value={values?.until}
                            onChange={(newDate) => setFieldValue('until', newDate)}
                            error={touched.time_slot && errors.time_slot}
                            helperText={touched.time_slot && errors.time_slot}
                        />
                    )} */}
                        </Grid>
                    </Grid>
                </>
            )}
        </GenericDialog>
    );
};

export default ReserveTableModal;
